import { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, Building2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const usernameRe = /^[A-Za-z0-9._-]{3,32}$/;

export default function Consortiums() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // consortium being edited
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: "",
    name: "",
    admin_username: "",
    admin_password: "",
    admin_name: "",
    active: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/consortiums");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ code: "", name: "", admin_username: "", admin_password: "", admin_name: "", active: true });
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    if (form.admin_username && !usernameRe.test(form.admin_username)) {
      toast.error("Usuario admin: 3-32 caracteres, solo letras, números, . _ -");
      return;
    }
    if (form.admin_username && form.admin_password.length < 6) {
      toast.error("La contraseña del admin debe tener al menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        active: form.active,
      };
      if (form.admin_username) {
        payload.admin_username = form.admin_username.trim().toLowerCase();
        payload.admin_password = form.admin_password;
        payload.admin_name = form.admin_name.trim() || undefined;
      }
      const { data } = await api.post("/consortiums", payload);
      toast.success(`Consorcio ${data.consortium.code} creado${data.admin ? ` · admin @${data.admin.username}` : ""}`);
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e?.preventDefault?.();
    if (!showEdit) return;
    setSaving(true);
    try {
      await api.put(`/consortiums/${showEdit.id}`, {
        code: showEdit.code,
        name: showEdit.name,
        active: showEdit.active,
      });
      toast.success("Consorcio actualizado");
      setShowEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el consorcio ${c.name}? Solo funciona si no tiene bancas ni usuarios.`)) return;
    try {
      await api.delete(`/consortiums/${c.id}`);
      toast.success("Consorcio eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Consorcios"
        subtitle="Cada consorcio agrupa múltiples bancas, admins y supervisores"
        testId="consortiums-header"
        actions={
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 rounded-sm h-10" data-testid="consortium-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo consorcio
          </Button>
        }
      />

      <div className="p-6 sm:p-8">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">Código</th>
                <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">Nombre</th>
                <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">Estado</th>
                <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">Creado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="5" className="p-6 text-center text-slate-500">Cargando…</td></tr>}
              {!loading && list.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-slate-500">Sin consorcios</td></tr>
              )}
              {list.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`consortium-row-${c.code}`}>
                  <td className="p-3 font-mono font-bold text-slate-950">{c.code}</td>
                  <td className="p-3 text-slate-800">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {c.name}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${c.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                      {c.active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs text-slate-600">{new Date(c.created_at).toLocaleDateString("es-DO")}</td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8 rounded-sm border-slate-300" onClick={() => setShowEdit({ ...c })} data-testid={`consortium-edit-${c.code}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-sm border-red-300 text-red-700 hover:bg-red-50" onClick={() => remove(c)} data-testid={`consortium-delete-${c.code}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-black tracking-tight">Nuevo consorcio</DialogTitle>
            <DialogDescription>
              Crea el consorcio y, opcionalmente, su usuario administrador en un solo paso.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Código</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="AZUA"
                  maxLength={20}
                  className="mt-1 rounded-sm border-slate-300 font-mono"
                  data-testid="consortium-code"
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Consorcio Azua"
                  className="mt-1 rounded-sm border-slate-300"
                  data-testid="consortium-name"
                  required
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 mb-2">
                Administrador del consorcio (opcional)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Usuario</Label>
                  <Input
                    value={form.admin_username}
                    onChange={(e) => setForm({ ...form, admin_username: e.target.value })}
                    placeholder="admazua"
                    className="mt-1 rounded-sm border-slate-300 font-mono lowercase"
                    autoCapitalize="none"
                    data-testid="consortium-admin-username"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Contraseña</Label>
                  <Input
                    type="password"
                    value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    placeholder="mín. 6 caracteres"
                    className="mt-1 rounded-sm border-slate-300"
                    data-testid="consortium-admin-password"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre completo</Label>
                  <Input
                    value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    placeholder="Administrador Azua"
                    className="mt-1 rounded-sm border-slate-300"
                    data-testid="consortium-admin-name"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Puedes crear el consorcio ahora y agregar administradores después desde <b>Usuarios</b>.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm border-slate-300" onClick={() => setShowCreate(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-sm" disabled={saving} data-testid="consortium-submit-create">
                {saving ? "Guardando…" : "Crear consorcio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!showEdit} onOpenChange={(o) => !o && setShowEdit(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-black tracking-tight">Editar consorcio</DialogTitle>
          </DialogHeader>
          {showEdit && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Código</Label>
                <Input
                  value={showEdit.code}
                  onChange={(e) => setShowEdit({ ...showEdit, code: e.target.value.toUpperCase() })}
                  className="mt-1 rounded-sm border-slate-300 font-mono"
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre</Label>
                <Input
                  value={showEdit.name}
                  onChange={(e) => setShowEdit({ ...showEdit, name: e.target.value })}
                  className="mt-1 rounded-sm border-slate-300"
                  required
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={showEdit.active} onCheckedChange={(v) => setShowEdit({ ...showEdit, active: v })} />
                <span className="text-sm text-slate-700">Activo</span>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-sm border-slate-300" onClick={() => setShowEdit(null)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-sm" disabled={saving} data-testid="consortium-submit-edit">
                  {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
